// ============================================================================
// AppointmentReminders — напоминания о записи (клиентская часть).
//
// Существующие серверные напоминания психологии (psy_bookings → due_remind)
// уходят СПЕЦИАЛИСТУ. Пациенту серверных напоминаний нет, поэтому здесь —
// честный клиентский механизм: настройка пользователя (settingsStore), список
// ближайших напоминаний и локальное уведомление/тост, пока приложение открыто.
// Ничего не пишется в медицинские данные и не создаёт новых RPC.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Bell, BellOff, BellRing, Clock3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToastStore } from '@/store/toastStore';
import { logSwallowed } from '@/lib/errors';
import {
  buildReminders,
  loadReminderSettings,
  markReminderFired,
  readFiredReminders,
  saveReminderSettings,
  valueDate,
  valueDateTimeLabel,
  type AppointmentItem,
  type AppointmentReminderSettings,
} from '@/lib/medicAppointments';

function permissionState(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

function relativeLabel(at: Date, now: Date): string {
  const diff = at.getTime() - now.getTime();
  if (diff <= 0) return 'сейчас';
  const minutes = Math.round(diff / 60_000);
  if (minutes < 60) return `через ${minutes} мин`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `через ${hours} ч`;
  return `через ${Math.round(hours / 24)} дн`;
}

export interface AppointmentRemindersProps {
  items: AppointmentItem[];
  mode: 'patient' | 'doctor';
}

export function AppointmentReminders({ items, mode }: AppointmentRemindersProps) {
  const [settings, setSettings] = useState<AppointmentReminderSettings>(() => loadReminderSettings());
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(() => permissionState());
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [firedRevision, setFiredRevision] = useState(0);

  // Тик раз в минуту — только для перерисовки «через N мин»; уведомления
  // ставятся таймерами, без опроса сервера.
  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  // Серверные напоминания psy_bookings уходят СПЕЦИАЛИСТУ (due_remind) —
  // в режиме врача не дублируем их локальными; локальные — для medic_encounters.
  const reminderItems = useMemo(
    () => (mode === 'doctor' ? items.filter((item) => item.source === 'core') : items),
    [items, mode],
  );

  const reminders = useMemo(
    () => (settings.enabled ? buildReminders(reminderItems, settings, new Date(nowMs)) : []),
    [reminderItems, settings, firedRevision, nowMs],
  );
  // Расписание таймеров НЕ зависит от минутного тика: иначе таймер у границы
  // срабатывания мог быть снят и переставлен, а напоминание — потеряно.
  // Пересчёт — раз в час (долгая сессия не теряет напоминания дальше 7 дней).
  const scheduleHour = Math.floor(nowMs / 3_600_000);
  const scheduleReminders = useMemo(
    () => (settings.enabled ? buildReminders(reminderItems, settings) : []),
    [reminderItems, settings, firedRevision, scheduleHour],
  );

  const fire = useCallback((key: string, item: AppointmentItem, leadHours: number) => {
    const when = valueDateTimeLabel(item.startAt);
    const who = mode === 'doctor'
      ? (item.patientName || 'пациент')
      : (item.doctorName || 'специалист');
    const title = `Запись на приём ${when}`;
    const body = `${who} · напоминание за ${leadHours >= 24 ? `${Math.round(leadHours / 24)} дн.` : `${leadHours} ч.`}`;
    if (permissionState() === 'granted') {
      try {
        new Notification(title, {
          body,
          tag: key,
          icon: '/base-icon',
        });
        return;
      } catch (e) {
        // Ошибка конструктора уведомления (ограничение окружения) — показываем
        // в приложении, чтобы напоминание не потерялось.
        logSwallowed(e, 'Запись на приём: системное уведомление');
      }
    }
    useToastStore.getState().showInfo(`${title} — ${body}`, { priority: true });
  }, [mode]);

  // Таймеры на ближайшие 7 дней. Fired-ключи не дают повторять напоминание
  // после перезагрузки/перерисовки.
  useEffect(() => {
    if (!settings.enabled) return;
    const fired = readFiredReminders();
    const timers: number[] = [];
    const now = Date.now();
    for (const reminder of scheduleReminders) {
      if (fired.has(reminder.key)) continue;
      const delay = reminder.at.getTime() - now;
      if (delay <= 0 || delay > 7 * 86_400_000) continue;
      const timer = window.setTimeout(() => {
        markReminderFired(reminder.key);
        setNowMs(Date.now());
        setFiredRevision((value) => value + 1);
        fire(reminder.key, reminder.item, reminder.leadHours);
      }, delay);
      timers.push(timer);
    }
    return () => {
      for (const timer of timers) window.clearTimeout(timer);
    };
  }, [scheduleReminders, settings.enabled, fire]);

  const requestPermission = async () => {
    if (!('Notification' in window)) {
      setPermission('unsupported');
      useToastStore.getState().showInfo('Уведомления браузера недоступны — напоминания покажем внутри приложения.');
      return;
    }
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'denied') {
        useToastStore.getState().showInfo('Уведомления браузера запрещены. Напоминания будут показываться внутри приложения.');
      }
    } catch (e) {
      setPermission('denied');
      useToastStore.getState().showInfo('Не удалось запросить разрешение на уведомления. Напоминания будут показываться внутри приложения.');
      logSwallowed(e, 'Запись на приём: разрешение уведомлений');
    }
  };

  const updateSettings = (next: AppointmentReminderSettings) => {
    setSettings(next);
    saveReminderSettings(next);
    if (next.enabled && permission === 'default') {
      void requestPermission();
    }
  };

  const toggleLead = (hours: number, checked: boolean) => {
    const next = checked
      ? [...new Set([...settings.leadHours, hours])].sort((a, b) => b - a)
      : settings.leadHours.filter((value) => value !== hours);
    if (next.length === 0) {
      useToastStore.getState().showWarning('Оставьте хотя бы одно напоминание или выключите их полностью.');
      return;
    }
    updateSettings({ ...settings, leadHours: next });
  };

  const now = new Date(nowMs);
  const visible = reminders.slice(0, 6);
  const upcomingCount = items.filter((item) => {
    const date = valueDate(item.startAt);
    return !!date && date.getTime() > now.getTime();
  }).length;

  return (
    <div className="rounded-xl border bg-card">
      <div className="flex flex-wrap items-center gap-2 border-b bg-muted/20 px-3 py-2">
        {settings.enabled ? <BellRing className="h-4 w-4 text-primary" /> : <BellOff className="h-4 w-4 text-muted-foreground" />}
        <span className="text-sm font-medium">Напоминания</span>
        <span className="text-[10px] text-muted-foreground">
          {settings.enabled ? `предстоящих записей: ${upcomingCount}` : 'выключены'}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {permission === 'default' && settings.enabled && (
            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => void requestPermission()}>
              <Bell className="mr-1 h-3 w-3" />
              Разрешить уведомления
            </Button>
          )}
          <label className="flex cursor-pointer items-center gap-1.5 text-[11px]">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-primary"
              checked={settings.enabled}
              onChange={(event) => updateSettings({ ...settings, enabled: event.target.checked })}
            />
            Напоминать
          </label>
        </div>
      </div>

      <div className="space-y-2 px-3 py-2">
        <div className="flex flex-wrap items-center gap-3 text-[11px]">
          <span className="text-muted-foreground">Когда:</span>
          {[24, 2].map((hours) => (
            <label key={hours} className="flex cursor-pointer items-center gap-1.5">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 accent-primary"
                checked={settings.leadHours.includes(hours)}
                disabled={!settings.enabled}
                onChange={(event) => toggleLead(hours, event.target.checked)}
              />
              за {hours >= 24 ? 'сутки' : '2 часа'}
            </label>
          ))}
          {permission === 'denied' && (
            <span className="text-[10px] text-amber-700 dark:text-amber-400">
              Уведомления браузера запрещены — напомним в приложении, пока оно открыто.
            </span>
          )}
          {permission === 'unsupported' && (
            <span className="text-[10px] text-muted-foreground">Браузер не поддерживает уведомления — напомним в приложении.</span>
          )}
        </div>

        {mode === 'doctor' && (
          <div className="text-[10px] text-muted-foreground">
            Психологические записи: напоминания специалисту отправляет сервер. Здесь — локальные
            напоминания по медицинским приёмам (medic_encounters).
          </div>
        )}
        {!settings.enabled && (
          <div className="rounded-lg border px-3 py-2 text-xs text-muted-foreground">
            Напоминания выключены. Включите «Напоминать», чтобы получать уведомления о предстоящих записях.
          </div>
        )}
        {settings.enabled && visible.length === 0 && (
          <div className="rounded-lg border px-3 py-2 text-xs text-muted-foreground">
            Ближайших напоминаний нет. Они появятся за {settings.leadHours
              .map((hours) => (hours >= 24 ? `${Math.round(hours / 24)} сут.` : `${hours} ч.`))
              .join(' / ')} до записи.
          </div>
        )}
        {settings.enabled && visible.map((reminder) => (
          <div key={reminder.key} className="flex items-start gap-2 rounded-lg border bg-background px-2.5 py-2">
            <Clock3 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium">
                {valueDateTimeLabel(reminder.item.startAt)}
                <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">{reminder.label}</span>
              </div>
              <div className="truncate text-[10px] text-muted-foreground">
                {mode === 'doctor'
                  ? (reminder.item.patientName || 'Пациент')
                  : (reminder.item.doctorName || 'Специалист')}
              </div>
            </div>
            <span className="shrink-0 text-[10px] text-muted-foreground">
              {relativeLabel(reminder.at, now)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
